import { HelpCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { ReactNode } from "react";
import { usePopover } from "./usePopover";
import { Floating } from "./Floating";

/**
 * The "why?" affordance (spec §9.19 "Explicit rules everywhere", §15): every computed value can show
 * its formula and inputs. Renders a small icon button and a popover with rows of label/value, drawn above the page
 * (`Floating`) so a widget's or the panel's edge never cuts it off.
 */
export function Why({
  title,
  rows,
  formula,
}: {
  title: ReactNode;
  rows: [ReactNode, ReactNode][];
  formula?: ReactNode;
}) {
  const { t } = useTranslation();
  const { open, toggle, ref, panelRef } = usePopover<HTMLSpanElement>();
  return (
    <span ref={ref} className="relative inline-flex">
      <button
        type="button"
        aria-label={t("why.label")}
        aria-expanded={open}
        onClick={toggle}
        className="grid h-5 w-5 place-items-center rounded-sm text-neutral-400 hover:bg-neutral-100 hover:text-neutral-900 dark:hover:bg-neutral-800 dark:hover:text-neutral-100"
      >
        <HelpCircle size={14} aria-hidden />
      </button>
      {open && (
        <Floating
          anchor={ref}
          panelRef={panelRef}
          role="dialog"
          aria-label={t("why.label")}
          data-testid="why-panel"
          className="w-72 max-w-[calc(100vw-1rem)] rounded-md border border-neutral-200 bg-[var(--surface)] p-3 text-sm font-normal normal-case tracking-normal text-neutral-900 shadow-[var(--shadow-md)] dark:border-neutral-700 dark:text-neutral-100"
        >
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.06em] text-neutral-500">{title}</p>
          {formula && (
            <p className="mb-2 font-mono text-[12px] text-neutral-700 dark:text-neutral-300">{formula}</p>
          )}
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[12.5px]">
            {rows.map(([k, v], i) => (
              <div key={i} className="contents">
                <dt className="text-neutral-500">{k}</dt>
                <dd className="font-mono text-neutral-900 dark:text-neutral-100">{v}</dd>
              </div>
            ))}
          </dl>
        </Floating>
      )}
    </span>
  );
}
